import React, { useState, useMemo, useRef } from 'react';
import './speaking.css';
import data from './Data/speakingtopics.json';
import Breadcrumbs from '../../Components/Breadcrumb';
import SEO from '../../Components/SEO';

const speakingTopicsImg = '/Images/speaking-topics-hero.webp';

const SpeakingTopics = () => {
  const topics = data;

  const [activeCategory, setActiveCategory] = useState(0);
  const [randomQuestion, setRandomQuestion] = useState(null);
  const [jollyCategory, setJollyCategory] = useState(''); // '' | number
  const [spotlightSource, setSpotlightSource] = useState(null); // 'jolly' | 'random' | null
  const [search, setSearch] = useState('');
  const mainRef = useRef(null);

  // Build the flat list of every question with its source category, once
  const allQuestions = useMemo(() => {
    const list = [];
    topics.forEach((topic, catIndex) => {
      if (!topic.content) return;
      topic.content.forEach((q) => {
        const text = (q || '').trim();
        if (!text || text.startsWith('---')) return;
        list.push({
          text,
          categoryIndex: catIndex,
          categoryTitle: topic.title
        });
      });
    });
    return list;
  }, [topics]);

  // Full pool the random button draws from. Excludes the active topic when a
  // search is running, so "next" surfaces something new instead of repeating.
  const randomPool = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allQuestions.filter(q => q.categoryIndex !== activeCategory &&
      (!term || q.text.toLowerCase().includes(term)));
  }, [allQuestions, search, activeCategory]);

  // Questions shown in the main list (search narrows within the active topic)
  const visibleQuestions = useMemo(() => {
    const topic = topics[activeCategory];
    if (!topic || !topic.content) return [];
    const term = search.trim().toLowerCase();
    return topic.content
      .map(t => (t || '').trim())
      .filter(t => t && !t.startsWith('---'))
      .filter(t => !term || t.toLowerCase().includes(term))
      .map(t => ({ text: t }));
  }, [topics, activeCategory, search]);

  const currentTopic = topics[activeCategory];

  // Roll from the full random pool (used by the Random question button)
  const rollRandom = () => {
    const pool = randomPool.length > 0 ? randomPool : allQuestions;
    if (pool.length === 0) {
      setRandomQuestion(null);
      return;
    }
    const pick = pool[Math.floor(Math.random() * pool.length)];
    setRandomQuestion(pick);
    setSpotlightSource('random');
  };

  // Roll from a specific topic (used by the Jolly Joker select)
  const rollFromTopic = (catIndex) => {
    const pool = allQuestions.filter(q => q.categoryIndex === catIndex);
    if (pool.length === 0) {
      setRandomQuestion(null);
      return;
    }
    const pick = pool[Math.floor(Math.random() * pool.length)];
    setRandomQuestion(pick);
    setSpotlightSource('jolly');
  };

  // "Next question" — respects whichever source the spotlight came from
  const rollNext = () => {
    if (spotlightSource === 'jolly' && jollyCategory !== '') {
      rollFromTopic(parseInt(jollyCategory));
    } else {
      rollRandom();
    }
  };

  const handleJollyChange = (e) => {
    const value = e.target.value;
    setJollyCategory(value);
    if (value === '') {
      setRandomQuestion(null);
      setSpotlightSource(null);
      return;
    }
    rollFromTopic(parseInt(value));
  };

  const clearRandom = () => {
    setRandomQuestion(null);
    setJollyCategory('');
    setSpotlightSource(null);
  };

  // Keep the search term when switching topics so the match counts stay useful,
  // and bring the list back into view
  const handleTopicChange = (e) => {
    setActiveCategory(parseInt(e.target.value));
    if (mainRef.current) mainRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Count matches per topic so the picker shows where the search term lives
  const matchCounts = useMemo(() => {
    const term = search.trim().toLowerCase();
    const counts = new Array(topics.length).fill(0);
    if (!term) return counts;
    topics.forEach((topic, i) => {
      if (!topic.content) return;
      counts[i] = topic.content.filter(t => {
        const v = (t || '').trim();
        return v && !v.startsWith('---') && v.toLowerCase().includes(term);
      }).length;
    });
    return counts;
  }, [topics, search]);

  const searchTerm = search.trim();

  return (
    <div className="speaking-page">
      <SEO
        title="Free English Speaking Topics and Discussion Questions"
        description="Practice speaking with 100+ topic categories and 3,700+ exam-style questions. Search every question, filter by topic, and get random prompts."
        name="English Exam Exercises"
        type="article"
        image={speakingTopicsImg}
      />

      <header className="speaking-header">
        <h1>Speaking Topics</h1>
        <p className="speaking-intro">
          Pick a topic, search all 3,700+ questions, or roll a random one.
        </p>
      </header>

      {/* Toolbar Select */}
      <div className="speaking-toolbar">
        <div className="speaking-toolbar-field">
          <label htmlFor="jolly-select">Jolly Joker</label>
          <select
            id="jolly-select"
            value={jollyCategory}
            onChange={handleJollyChange}
          >
            <option value="">Pick a topic…</option>
            {topics.map((topic, index) => (
              <option key={index} value={index}>{topic.title}</option>
            ))}
          </select>
        </div>

        {/* Random question spotlight — inline in toolbar */}
        {randomQuestion && (
          <div className="speaking-spotlight">
            <div className="speaking-spotlight-header">
              <span className="speaking-spotlight-cat">{randomQuestion.categoryTitle}</span>
              <button
                className="speaking-spotlight-close"
                onClick={clearRandom}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className="speaking-spotlight-question">{randomQuestion.text}</p>
            <button className="speaking-spotlight-next" onClick={rollNext}>
              Next question →
            </button>
          </div>
        )}

        <button className="speaking-random-btn" onClick={rollRandom}>
          🎲 Random question
        </button>
      </div>

      {/* Main layout — single column now */}
      <div className="speaking-layout">
        <main className="speaking-main" ref={mainRef}>
          <div className="speaking-toolbar-field">
            <label htmlFor="topic-select">Topic List</label>
            <select
              id="topic-select"
              value={activeCategory}
              onChange={handleTopicChange}
            >
{topics.map((topic, index) => (
              <option key={index} value={index}>
                {searchTerm && matchCounts[index] > 0
                  ? `${topic.title} (${matchCounts[index]})`
                  : topic.title}
              </option>
            ))}
            </select>
          </div>

          <div className="speaking-search-field">
            <label htmlFor="question-search">Search questions</label>
            <div className="speaking-search-wrap">
              <input
                id="question-search"
                type="search"
                value={search}
                placeholder="e.g. pets, work, advice…"
                onChange={(e) => setSearch(e.target.value)}
              />
              {searchTerm && (
                <button
                  className="speaking-search-clear"
                  onClick={() => setSearch('')}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
            {searchTerm && (
              <span className="speaking-search-hint">
                {visibleQuestions.length} here
              </span>
            )}
          </div>

          <div className="speaking-main-header">
            <h2>{currentTopic?.title}</h2>
            <span className="speaking-count">
              {searchTerm
                ? `${visibleQuestions.length} of ${currentTopic?.content?.length ?? 0} matching`
                : `${visibleQuestions.length} ${visibleQuestions.length === 1 ? 'question' : 'questions'}`}
            </span>
          </div>

          {visibleQuestions.length === 0 ? (
            <p className="speaking-empty">
              {searchTerm
                ? <>No questions here match “{searchTerm}”. Try another topic from the list above.</>
                : 'No questions match this filter.'}
            </p>
          ) : (
            <ul className="speaking-question-list">
              {visibleQuestions.map((q, i) => (
                <li key={i} className="speaking-question-row">
                  <span className="speaking-q-text">{q.text}</span>
                </li>
              ))}
            </ul>
          )}
        </main>
      </div>
    </div>
  );
};

export default SpeakingTopics;